/**
 * AdminLayout — sticky sidebar (desktop) + topbar + mobile bottom-nav.
 *
 * The admin is intentionally lighter in chrome than the public site —
 * dense tables, weiße Cards, sandBeige borders. Pastel accents only.
 */
import { useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Bell,
  Building2,
  ChevronDown,
  ClipboardList,
  Coins,
  FileText,
  Home,
  Image,
  LayoutDashboard,
  Link2,
  LogOut,
  MessageSquare,
  Settings,
  Sparkles,
  Star,
  Users,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const NAV_ITEMS = [
  { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard, enabled: true },
  { to: "/admin/startups", label: "Startups", icon: Building2, enabled: true },
  { to: "/admin/investors", label: "Investors", icon: Coins, enabled: true },
  { to: "/admin/contacts", label: "Contacts", icon: Users, enabled: true },
  { to: "/admin/audit", label: "Audit", icon: ClipboardList, enabled: true },
  { to: "/admin/magic-link", label: "Magic Link", icon: Link2, enabled: true },
  // Future phases — visible but disabled, signals progression
  { to: "#", label: "Matches", icon: Sparkles, enabled: false, phase: "P8" },
  { to: "#", label: "Messages", icon: MessageSquare, enabled: false, phase: "P10" },
  { to: "#", label: "Bookings", icon: Star, enabled: false, phase: "P10" },
  { to: "#", label: "Content", icon: FileText, enabled: false, phase: "P10" },
  { to: "#", label: "Media", icon: Image, enabled: false, phase: "P10" },
  { to: "#", label: "Settings", icon: Settings, enabled: false, phase: "P5" },
];

const MOBILE_ITEMS = [
  { to: "/admin/dashboard", label: "Home", icon: Home },
  { to: "/admin/startups", label: "Startups", icon: Building2 },
  { to: "/admin/investors", label: "Investors", icon: Coins },
  { to: "/admin/audit", label: "Audit", icon: ClipboardList },
  { to: "/admin/contacts", label: "Contacts", icon: Users },
];

const TITLE_BY_PATH = {
  "/admin/dashboard": "Übersicht",
  "/admin/startups": "Startups",
  "/admin/investors": "Investor:innen",
  "/admin/contacts": "Kontakte",
  "/admin/audit": "Audit Log",
  "/admin/magic-link": "Magic Link",
};

const titleFor = (pathname) => {
  if (TITLE_BY_PATH[pathname]) return TITLE_BY_PATH[pathname];
  for (const key of Object.keys(TITLE_BY_PATH)) {
    if (pathname.startsWith(key)) return TITLE_BY_PATH[key];
  }
  return "Admin";
};

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [profileOpen, setProfileOpen] = useState(false);

  const onLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-porcelain text-cocoa flex">
      {/* Sidebar — desktop only */}
      <aside
        className="hidden md:flex md:w-64 lg:w-72 md:flex-col md:fixed md:inset-y-0 md:left-0 bg-white border-r border-sandBeige z-30"
        data-testid="admin-sidebar"
      >
        <div className="px-7 py-7 border-b border-sandBeige">
          <p className="font-serif text-2xl text-cocoa lowercase tracking-tight">
            womenintech<span className="text-raspberry">.life</span>
          </p>
          <p className="text-xs text-warmGrey mt-1">Admin · Phase 4</p>
        </div>
        <nav className="flex-1 px-4 py-5 overflow-y-auto">
          <ul className="flex flex-col gap-1">
            {NAV_ITEMS.map(({ to, label, icon: Icon, enabled, phase }) => {
              if (!enabled) {
                return (
                  <li key={label} className="opacity-50">
                    <span
                      className="flex items-center gap-3 px-3 py-2 rounded-2xl text-sm text-warmGrey"
                      data-testid={`sidebar-disabled-${label.toLowerCase()}`}
                    >
                      <Icon size={16} />
                      <span className="flex-1">{label}</span>
                      <span className="text-[10px] uppercase tracking-wide text-warmGrey/80">
                        {phase}
                      </span>
                    </span>
                  </li>
                );
              }
              return (
                <li key={to}>
                  <NavLink
                    to={to}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 rounded-2xl text-sm transition-colors ${
                        isActive
                          ? "bg-roseMilk/70 text-cocoa font-medium"
                          : "text-warmGrey hover:text-cocoa hover:bg-ivory"
                      }`
                    }
                    data-testid={`sidebar-${label.toLowerCase()}`}
                  >
                    <Icon size={16} />
                    {label}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="px-4 pb-5 pt-3 border-t border-sandBeige relative">
          <button
            type="button"
            onClick={() => setProfileOpen((v) => !v)}
            className="w-full flex items-center gap-3 p-2 rounded-2xl hover:bg-ivory transition-colors"
            data-testid="sidebar-profile-toggle"
          >
            <div className="w-9 h-9 rounded-full bg-raspberry text-white flex items-center justify-center font-medium text-sm">
              {(user?.name || "A").slice(0, 1)}
            </div>
            <div className="flex-1 text-left min-w-0">
              <p className="text-sm font-medium text-cocoa truncate">{user?.name}</p>
              <p className="text-xs text-warmGrey truncate">{user?.email}</p>
            </div>
            <ChevronDown size={14} className="text-warmGrey" />
          </button>
          {profileOpen && (
            <div className="absolute bottom-16 left-4 right-4 bg-white border border-sandBeige rounded-2xl shadow-soft p-2 z-40">
              <button
                type="button"
                onClick={onLogout}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-cocoa hover:bg-ivory"
                data-testid="sidebar-logout"
              >
                <LogOut size={14} /> Ausloggen
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Main column */}
      <div className="flex-1 md:ml-64 lg:ml-72 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="sticky top-0 z-20 bg-porcelain/95 backdrop-blur border-b border-sandBeige px-5 md:px-8 h-16 flex items-center gap-4">
          <h1 className="font-serif text-xl md:text-2xl text-cocoa truncate" data-testid="topbar-title">
            {titleFor(location.pathname)}
          </h1>
          <div className="flex-1" />
          <button
            type="button"
            aria-label="Benachrichtigungen"
            className="relative w-9 h-9 rounded-full bg-white border border-sandBeige flex items-center justify-center hover:border-raspberry transition-colors"
            data-testid="topbar-bell"
          >
            <Bell size={15} className="text-cocoa" />
          </button>
          <div className="md:hidden w-9 h-9 rounded-full bg-raspberry text-white flex items-center justify-center text-sm font-medium">
            {(user?.name || "A").slice(0, 1)}
          </div>
        </header>

        <main className="flex-1 px-5 md:px-8 py-6 pb-28 md:pb-10" data-testid="admin-main">
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom-nav */}
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 bg-white border-t border-sandBeige z-30"
        aria-label="Mobile admin nav"
        data-testid="admin-bottom-nav"
      >
        <ul className="flex justify-around items-stretch">
          {MOBILE_ITEMS.map(({ to, label, icon: Icon }) => (
            <li key={to} className="flex-1">
              <NavLink
                to={to}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center gap-1 py-3 text-[11px] ${
                    isActive ? "text-raspberry" : "text-warmGrey"
                  }`
                }
                data-testid={`bottomnav-${label.toLowerCase()}`}
              >
                <Icon size={18} />
                <span>{label}</span>
              </NavLink>
            </li>
          ))}
          <li className="flex-1">
            <button
              type="button"
              onClick={onLogout}
              className="w-full flex flex-col items-center justify-center gap-1 py-3 text-[11px] text-warmGrey"
              data-testid="bottomnav-logout"
            >
              <LogOut size={18} />
              <span>Logout</span>
            </button>
          </li>
        </ul>
      </nav>
    </div>
  );
}
